import { ReactNode, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { defineAbilitiesFor, PlatformAction, PlatformSubject } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';
import { ForbiddenPage } from './ForbiddenPage';

/**
 * Route-level permission gate. Renders `children` only if the current
 * user can perform `action` on `subject`; otherwise renders the 403
 * ForbiddenPage (UX-09). When `redirectTo` is passed explicitly the old
 * behaviour is kept and the user is redirected there instead.
 *
 * Uses the same ability context as the <Can> component. The server is
 * still authoritative — this only keeps the UI honest so a user who
 * lacks the capability never sees the admin surface.
 */
export function RequireAbility({
    action,
    subject,
    redirectTo,
    children,
}: {
    action: PlatformAction;
    subject: PlatformSubject;
    redirectTo?: string;
    children: ReactNode;
}): ReactNode {
    const role = useAuthStore((s) => s.user?.role);
    const ability = useMemo(() => defineAbilitiesFor(role), [role]);
    if (!ability.can(action, subject)) {
        if (redirectTo) return <Navigate to={redirectTo} replace />;
        return <ForbiddenPage action={action} subject={subject} />;
    }
    return <>{children}</>;
}
